import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import CursorDot from "@/components/site/CursorDot";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import AsciiDivider from "@/components/site/AsciiDivider";
import Seo from "@/components/site/Seo";
import { getProjectBySlug, getAdjacentProjects } from "@/data/projects";

export default function ProjectPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const project = getProjectBySlug(slug);
  const { prev, next } = getAdjacentProjects(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) document.title = `${project.title} — JP Kuster`;
  }, [slug, project]);

  if (!project) {
    return (
      <main className="grain min-h-screen bg-[#F2EFE9]" data-testid="project-not-found">
        <Seo title="404 — Off the Map" description="That project isn't here yet." path={`/project/${slug}`} />
        <CursorDot />
        <Nav />
        <section className="px-4 md:px-8 lg:px-12 pt-40 pb-32">
          <div className="section-label mb-3 text-[#4A4A4A]">§ 404 — Off the Map</div>
          <h1 className="font-anton uppercase text-6xl md:text-8xl leading-[0.9]">
            That project
            <span className="font-cormorant italic text-[#D83127] font-semibold"> isn&apos;t </span>
            here yet.
          </h1>
          <button onClick={() => nav("/")} className="btn-offset mt-8" data-testid="project-404-back">
            ← Back to Home
          </button>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="grain bg-[#F2EFE9]" data-testid={`project-page-${project.slug}`}>
      <Seo
        title={project.title}
        description={`${project.summary} Category: ${project.category}. Client: ${project.client}.`}
        path={`/project/${project.slug}`}
        image={project.hero}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: project.title,
          headline: project.title,
          description: project.summary,
          image: project.hero,
          url: `https://jpkuster.com/project/${project.slug}`,
          creator: { "@type": "Person", name: "John \"JP\" Kuster" },
          dateCreated: String(project.year),
          genre: project.category,
          about: project.client,
        }}
      />
      <CursorDot />
      <Nav />

      {/* Title block */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-16 px-4 md:px-8 lg:px-12" data-testid="project-header">
        <div className="mb-8">
          <Link
            to="/#work"
            className="font-mono-s text-xs tracking-[0.25em] uppercase link-underline"
            data-testid="project-back-link"
          >
            ← Back to Work
          </Link>
        </div>

        <div className="mb-8 max-w-5xl">
          <div className="flex items-center gap-4 font-mono-s text-xs tracking-[0.25em] uppercase text-[#4A4A4A] mb-4">
            <span>{project.category}</span>
            <span>/</span>
            <span>{project.year}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-anton uppercase text-5xl md:text-7xl lg:text-[7rem] leading-[0.92]"
            data-testid="project-title"
          >
            {project.title}
          </motion.h1>
          <p className="font-cormorant italic text-xl md:text-2xl leading-snug mt-6 max-w-3xl">
            {project.tagline}
          </p>
        </div>
      </section>

      {/* Hero image */}
      <section className="px-4 md:px-8 lg:px-12">
        <div className="hard-border overflow-hidden bg-[#111]">
          <img
            src={project.hero}
            alt={project.title}
            className="w-full h-auto block"
            loading="eager"
            data-testid="project-hero-image"
          />
        </div>
      </section>

      <AsciiDivider char="/" label="§ Brief" />

      {/* Meta + summary */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 grid grid-cols-12 gap-6" data-testid="project-brief">
        <div className="col-span-12 md:col-span-4">
          <dl className="space-y-4">
            <div>
              <dt className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]">Client</dt>
              <dd className="font-anton uppercase text-2xl md:text-3xl mt-1">{project.client}</dd>
            </div>
            <div>
              <dt className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]">Role</dt>
              <dd className="font-cormorant italic text-lg md:text-xl mt-1">{project.role}</dd>
            </div>
            <div>
              <dt className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A]">Timeline</dt>
              <dd className="font-mono-s text-sm mt-1 uppercase tracking-[0.15em]">{project.timeline}</dd>
            </div>
          </dl>
        </div>
        <div className="col-span-12 md:col-span-8">
          <p className="font-work text-xl md:text-2xl leading-relaxed">{project.summary}</p>
        </div>
      </section>

      {/* Metrics ribbon */}
      <section className="hard-border-t hard-border-b bg-[#111111] text-[#F2EFE9] grid grid-cols-1 md:grid-cols-3" data-testid="project-metrics">
        {project.metrics.map((m, i) => (
          <div
            key={i}
            className={`p-8 md:p-12 ${i < project.metrics.length - 1 ? "border-b md:border-b-0 md:border-r border-[#333]" : ""}`}
          >
            <div className="font-anton uppercase text-5xl md:text-6xl lg:text-7xl leading-none">{m.k}</div>
            <div className="font-mono-s text-[10px] md:text-xs tracking-[0.25em] uppercase mt-3 text-[#F2EFE9]/70">
              {m.v}
            </div>
          </div>
        ))}
      </section>

      {/* Challenge + Approach + Outcome */}
      <section className="px-4 md:px-8 lg:px-12 py-20 md:py-28" data-testid="project-story">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-5">
            <div className="section-label mb-3 text-[#4A4A4A]">§ Challenge</div>
            <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.92]">
              The{" "}
              <span className="font-cormorant italic text-[#D83127] font-semibold">problem </span>
              on the table.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-7 font-work text-lg md:text-xl leading-relaxed self-end">
            {project.challenge}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-20 mb-10">
          <div className="col-span-12 md:col-span-5">
            <div className="section-label mb-3 text-[#4A4A4A]">§ Approach</div>
            <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.92]">
              How it
              <span className="font-cormorant italic text-[#2E4F36] font-semibold"> got </span>
              built.
            </h2>
          </div>
          <ul className="col-span-12 md:col-span-7 space-y-4 self-end">
            {project.approach.map((a, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex gap-4 font-work text-lg md:text-xl leading-relaxed border-t border-[#111]/30 pt-4"
              >
                <span className="font-mono-s text-sm tracking-[0.25em] text-[#D83127] shrink-0 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{a}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-20">
          <div className="col-span-12 md:col-span-5">
            <div className="section-label mb-3 text-[#4A4A4A]">§ Outcome</div>
            <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.92]">
              The
              <span className="font-cormorant italic text-[#D83127] font-semibold"> outcome.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-7 font-work text-lg md:text-xl leading-relaxed self-end">
            {project.outcome}
          </p>
        </div>
      </section>

      {project.sections && project.sections.length > 0 ? (
        <>
          <AsciiDivider char="_" label={`Gallery · ${project.sections.length} chapters`} />
          {project.sections.map((sec, si) => {
            const layoutCls =
              sec.layout === "feature"
                ? "grid grid-cols-12 gap-4 md:gap-6"
                : sec.layout === "wide"
                ? "grid grid-cols-1 gap-4 md:gap-6"
                : sec.layout === "portrait"
                ? "grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
                : "grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6";
            const aspectCls =
              sec.layout === "wide"
                ? "aspect-[21/9]"
                : sec.layout === "portrait"
                ? "aspect-[3/4]"
                : "aspect-square";
            return (
              <section
                key={si}
                className="px-4 md:px-8 lg:px-12 py-14 md:py-20 hard-border-t"
                data-testid={`project-section-${si}`}
              >
                <div className="grid grid-cols-12 gap-6 mb-8 md:mb-10">
                  <div className="col-span-12 md:col-span-5">
                    <div className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] mb-2">
                      Chapter {String(si + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-anton uppercase text-4xl md:text-6xl leading-[0.95]">
                      {sec.title}
                    </h3>
                  </div>
                  {sec.description && (
                    <p className="col-span-12 md:col-span-6 md:col-start-7 font-cormorant italic text-lg md:text-xl leading-snug self-end text-[#1a1a1a]">
                      {sec.description}
                    </p>
                  )}
                </div>
                <div className={layoutCls}>
                  {sec.images.map((src, ii) => {
                    const featureSpan = sec.layout === "feature"
                      ? ii % 5 === 0
                        ? "col-span-12 md:col-span-8"
                        : ii % 5 === 1
                        ? "col-span-12 md:col-span-4"
                        : "col-span-6 md:col-span-4"
                      : "";
                    return (
                      <motion.figure
                        key={ii}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: (ii % 4) * 0.05 }}
                        className={`hard-border overflow-hidden bg-[#111] ${featureSpan} ${sec.layout === "feature" ? "aspect-[4/3]" : aspectCls}`}
                        data-testid={`project-section-${si}-item-${ii}`}
                      >
                        <img
                          src={src}
                          alt={`${project.title} — ${sec.title} ${ii + 1}`}
                          className="w-full h-full object-cover block transition-transform duration-700 hover:scale-[1.03]"
                          loading="lazy"
                        />
                      </motion.figure>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </>
      ) : (
        <>
          <AsciiDivider char="_" label={`Gallery · ${project.gallery.length} pieces`} />
          <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20" data-testid="project-gallery">
            <div className="grid grid-cols-12 gap-4 md:gap-6">
              {project.gallery.map((src, i) => {
                const pattern = i % 7;
                let span = "col-span-12 md:col-span-6";
                if (pattern === 0) span = "col-span-12 md:col-span-8";
                else if (pattern === 1) span = "col-span-12 md:col-span-4";
                else if (pattern === 3) span = "col-span-6 md:col-span-4";
                else if (pattern === 4) span = "col-span-6 md:col-span-4";
                else if (pattern === 5) span = "col-span-12 md:col-span-4";
                return (
                  <motion.figure
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                    className={`${span} hard-border overflow-hidden bg-[#111] aspect-[4/3]`}
                    data-testid={`project-gallery-item-${i}`}
                  >
                    <img
                      src={src}
                      alt={`${project.title} — ${i + 2}`}
                      className="w-full h-full object-cover block transition-transform duration-700 hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </motion.figure>
                );
              })}
            </div>
          </section>
        </>
      )}

      {project.press && project.press.length > 0 && (
        <section
          className="px-4 md:px-8 lg:px-12 py-16 md:py-20 hard-border-t bg-[#E8E4DB]"
          data-testid="project-press"
        >
          <div className="grid grid-cols-12 gap-6 mb-8">
            <div className="col-span-12 md:col-span-5">
              <div className="section-label mb-3 text-[#4A4A4A]">§ In the Press</div>
              <h3 className="font-anton uppercase text-4xl md:text-6xl leading-[0.92]">
                Coverage &amp;
                <span className="font-cormorant italic text-[#D83127] font-semibold"> citations.</span>
              </h3>
            </div>
          </div>
          <ul className="hard-border-t">
            {project.press.map((p, i) => (
              <li key={i} className="hard-border-b">
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6 py-4 md:py-5 hover:bg-[#F2EFE9] transition-colors px-2"
                  data-testid={`project-press-${i}`}
                >
                  <span className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] md:w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-anton uppercase text-2xl md:text-3xl leading-tight flex-1 group-hover:text-[#D83127] transition-colors">
                    {p.title}
                  </span>
                  <span className="font-mono-s text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#1a1a1a] shrink-0">
                    {p.source} →
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Prev / Next */}
      <section
        className="px-4 md:px-8 lg:px-12 py-16 hard-border-t grid grid-cols-1 md:grid-cols-2 gap-0"
        data-testid="project-pagination"
      >
        {prev && (
          <Link
            to={`/project/${prev.slug}`}
            className="group block md:border-r border-[#111] pr-0 md:pr-8 py-6"
            data-testid="project-prev-link"
          >
            <div className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] mb-2">
              ← Previous
            </div>
            <div className="font-anton uppercase text-3xl md:text-5xl group-hover:text-[#D83127] transition-colors leading-tight">
              {prev.title}
            </div>
            <div className="font-cormorant italic text-base mt-1 text-[#1a1a1a]">
              {prev.category} · {prev.year}
            </div>
          </Link>
        )}
        {next && (
          <Link
            to={`/project/${next.slug}`}
            className="group block pl-0 md:pl-8 py-6 text-right"
            data-testid="project-next-link"
          >
            <div className="font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] mb-2">
              Next →
            </div>
            <div className="font-anton uppercase text-3xl md:text-5xl group-hover:text-[#D83127] transition-colors leading-tight">
              {next.title}
            </div>
            <div className="font-cormorant italic text-base mt-1 text-[#1a1a1a]">
              {next.category} · {next.year}
            </div>
          </Link>
        )}
      </section>

      {/* CTA */}
      <section className="bg-[#111111] text-[#F2EFE9] px-4 md:px-8 lg:px-12 py-20 md:py-28" data-testid="project-cta">
        <div className="font-mono-s text-[11px] tracking-[0.3em] uppercase text-[#F2EFE9]/60 mb-3">
          Considering similar work?
        </div>
        <h2 className="font-anton uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
          Let&apos;s build
          <span className="font-cormorant italic text-[#D83127] font-semibold"> yours </span>
          next.
        </h2>
        <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
          <Link to="/#contact" className="btn-offset" data-testid="project-cta-contact">
            Start a Project →
          </Link>
          <Link to="/#work" className="btn-offset inverse" data-testid="project-cta-more-work">
            See More Work
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
